import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Avatar,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputBase,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Skeleton,
  Typography,
} from "@mui/material";
import { Search as SearchIcon, Close as CloseIcon } from "@mui/icons-material";
import { fetchInboxTeamData } from "../../redux/Slice/Inbox/inboxSettingsSlice";
import { sortByName } from "../../utils/sortByName";

const RowSkeleton = () => (
  <ListItem>
    <Skeleton variant="circular" width={32} height={32} sx={{ mr: 1.25 }} />
    <Skeleton variant="text" width="58%" height={24} />
  </ListItem>
);

const normalizeTeam = (team) => ({
  id: team?.id || team?._id,
  name: team?.teamName || "Team",
  type: "team",
});

const normalizeMember = (member) => ({
  id: member?.id || member?._id,
  name: member?.name || "Team Member",
  type: "teammember",
});

const TeamAssigneePickerDialog = ({
  open,
  onClose,
  onApply,
  multiple = false,
  selectedItems = [],
  title = "Select assignee",
}) => {
  const dispatch = useDispatch();
  const { loading, teams, teamMembers } = useSelector((state) => state.inboxSettings);
  const [search, setSearch] = useState("");
  const [localSelected, setLocalSelected] = useState(selectedItems);

  useEffect(() => {
    if (open) {
      dispatch(fetchInboxTeamData());
      setSearch("");
    }
  }, [dispatch, open]);

  useEffect(() => {
    if (open) {
      setLocalSelected(selectedItems);
    }
  }, [open, selectedItems]);

  const normalizedTeams = useMemo(
    () => (Array.isArray(teams) ? sortByName(teams.map(normalizeTeam)) : []),
    [teams]
  );
  const normalizedMembers = useMemo(
    () => (Array.isArray(teamMembers) ? sortByName(teamMembers.map(normalizeMember)) : []),
    [teamMembers]
  );

  const query = search.trim().toLowerCase();
  const filteredTeams = normalizedTeams.filter((x) => x.name.toLowerCase().includes(query));
  const filteredMembers = normalizedMembers.filter((x) => x.name.toLowerCase().includes(query));

  const isSelected = (item) =>
    localSelected.some((x) => x.id === item.id && x.type === item.type);

  const handlePick = (item) => {
    if (!multiple) {
      onApply([item]);
      onClose();
      return;
    }

    setLocalSelected((prev) => {
      const exists = prev.some((x) => x.id === item.id && x.type === item.type);
      if (exists) {
        return prev.filter((x) => !(x.id === item.id && x.type === item.type));
      }
      return [...prev, item];
    });
  };

  const removeSelected = (item) => {
    setLocalSelected((prev) =>
      prev.filter((x) => !(x.id === item.id && x.type === item.type))
    );
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Typography sx={{ fontWeight: 700, fontSize: { xs: "1rem", sm: "1.08rem", md: "1.12rem" } }}>
          {title}
        </Typography>
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ p: 0 }}>
        <Box sx={{ p: 1.5 }}>
          <Box
            sx={{
              border: (theme) => `1px solid ${theme.palette.divider}`,
              borderRadius: 1.2,
              display: "flex",
              alignItems: "center",
              flexWrap: "wrap",
              minHeight: 42,
              px: 1,
              py: 0.5,
              gap: 0.5,
            }}
          >
            {multiple &&
              localSelected.map((item) => (
                <Chip
                  key={`sel_${item.type}_${item.id}`}
                  size="small"
                  label={item.name}
                  onDelete={() => removeSelected(item)}
                />
              ))}
            <InputBase
              placeholder="Search teams and members"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              sx={{
                flex: 1,
                minWidth: 120,
                "& input": {
                  fontSize: { xs: "0.9rem", sm: "0.95rem", md: "0.98rem" },
                },
              }}
            />
            <SearchIcon sx={{ fontSize: { xs: "1rem", sm: "1.05rem" }, color: "text.secondary" }} />
          </Box>
        </Box>

        <Box sx={{ maxHeight: 430, overflowY: "auto", pb: 1 }}>
          <Box sx={{ px: 2, pb: 0.5 }}>
            <Typography sx={{ fontSize: { xs: "0.82rem", sm: "0.86rem", md: "0.9rem" }, fontWeight: 700, color: "text.secondary", textTransform: "uppercase" }}>
              Teams
            </Typography>
          </Box>
          <Divider sx={{ mx: 2 }} />
          <List dense sx={{ py: 0.5 }}>
            {loading ? (
              <>
                <RowSkeleton />
                <RowSkeleton />
                <RowSkeleton />
              </>
            ) : filteredTeams.length === 0 ? (
              <ListItem>
                <ListItemText primary="No teams found" />
              </ListItem>
            ) : (
              filteredTeams.map((team) => (
                <ListItem key={`t_${team.id}`} disablePadding sx={{ px: 1 }}>
                  <ListItemButton onClick={() => handlePick(team)} sx={{ borderRadius: 1 }}>
                    {multiple && (
                      <Checkbox
                        size="small"
                        checked={isSelected(team)}
                        onChange={() => handlePick(team)}
                        onClick={(e) => e.stopPropagation()}
                      />
                    )}
                    <Avatar sx={{ width: 32, height: 32, mr: 1.25, fontSize: "0.9rem" }}>
                      {team.name[0]}
                    </Avatar>
                    <ListItemText
                      primary={team.name}
                      primaryTypographyProps={{
                        fontSize: { xs: "0.9rem", sm: "0.95rem", md: "0.98rem" },
                        fontWeight: 500,
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              ))
            )}
          </List>

          <Box sx={{ px: 2, pb: 0.5, pt: 1 }}>
            <Typography sx={{ fontSize: { xs: "0.82rem", sm: "0.86rem", md: "0.9rem" }, fontWeight: 700, color: "text.secondary", textTransform: "uppercase" }}>
              Team Members
            </Typography>
          </Box>
          <Divider sx={{ mx: 2 }} />
          <List dense sx={{ py: 0.5 }}>
            {loading ? (
              <>
                <RowSkeleton />
                <RowSkeleton />
                <RowSkeleton />
              </>
            ) : filteredMembers.length === 0 ? (
              <ListItem>
                <ListItemText primary="No team members found" />
              </ListItem>
            ) : (
              filteredMembers.map((member) => (
                <ListItem key={`m_${member.id}`} disablePadding sx={{ px: 1 }}>
                  <ListItemButton onClick={() => handlePick(member)} sx={{ borderRadius: 1 }}>
                    {multiple && (
                      <Checkbox
                        size="small"
                        checked={isSelected(member)}
                        onChange={() => handlePick(member)}
                        onClick={(e) => e.stopPropagation()}
                      />
                    )}
                    <Avatar sx={{ width: 32, height: 32, mr: 1.25, fontSize: "0.9rem" }}>
                      {member.name[0]}
                    </Avatar>
                    <ListItemText
                      primary={member.name}
                      primaryTypographyProps={{
                        fontSize: { xs: "0.9rem", sm: "0.95rem", md: "0.98rem" },
                        fontWeight: 500,
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              ))
            )}
          </List>
        </Box>
      </DialogContent>
      {multiple && (
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => {
              onApply(localSelected);
              onClose();
            }}
          >
            Apply
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default TeamAssigneePickerDialog;
